"use client";

import { useEffect, useState, useMemo } from "react";
import { formatBalance } from "@cede/utils";
import { ClaimRewards } from "@/components/features/rewards/Reward/ClaimRewards";
import { UsdtIcon } from "@/components/icons";
import { InfoCard } from "@/components/ui/info-card";
import { SkeletonElement } from "@/components/ui/skeleton";
import { useUserStats, useCampaignClaimRewards, useDevice } from "@/hooks";

export const ClaimableNowCard = () => {
  const { isMobile } = useDevice();
  const { stats, isLoading } = useUserStats();
  const { claimableSoonEpochs } = useCampaignClaimRewards({});

  const [timeRemaining, setTimeRemaining] = useState<string>("");
  const [progress, setProgress] = useState<number>(0);

  const claimableNowValue = stats?.claimableNow?.value || 0;
  const claimableSoonValue = stats?.claimableSoon?.value || 0;

  // Find the nearest unlock time from claimableSoonEpochs
  const nearestEpoch = useMemo(() => {
    if (!claimableSoonEpochs || claimableSoonEpochs.length === 0) return null;

    const now = new Date().getTime();
    const sorted = claimableSoonEpochs
      .map((epoch) => ({
        ...epoch,
        unlockTime: new Date(epoch.cooldownEnd || epoch.epochEnd).getTime(),
      }))
      .filter((epoch) => epoch.unlockTime > now)
      .sort((a, b) => a.unlockTime - b.unlockTime);

    return sorted[0] || null;
  }, [claimableSoonEpochs]);

  // Update countdown timer
  useEffect(() => {
    if (!nearestEpoch) {
      setTimeRemaining("");
      setProgress(0);
      return;
    }

    const COOLDOWN_DURATION_MS = 24 * 60 * 60 * 1000;

    const updateTimer = () => {
      const now = Date.now();
      const distance = nearestEpoch.unlockTime - now;

      if (distance < 0) {
        setTimeRemaining("");
        setProgress(100);
        return;
      }

      const hours = Math.floor(distance / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      setTimeRemaining(`${hours}h ${minutes}m ${seconds}s`);
      // Simple linear progress: start 24h before unlock, finish at unlock
      const cooldownStart = nearestEpoch.unlockTime - COOLDOWN_DURATION_MS;
      const elapsed = now - cooldownStart;
      const progressPercentage = (elapsed / COOLDOWN_DURATION_MS) * 100;
      setProgress(Math.min(Math.max(progressPercentage, 0), 100));
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [nearestEpoch]);

  return (
    <InfoCard
      title="Claimable Now"
      padding={isMobile ? "none" : "md"}
      withBackground={!isMobile}
      withBorder={!isMobile}
      gap="lg"
      className="flex min-w-0 flex-col"
      contentClassName="flex flex-1 flex-col justify-between gap-5"
      titleClassName="text-lg font-semibold text-cede-text-primary"
    >
      <div className="flex flex-col gap-5">
        <div className="flex items-center gap-2">
          {isLoading ? (
            <SkeletonElement className="h-[60px] w-[120px] rounded-lg" />
          ) : (
            <span className="text-5xl font-bold text-cede-text-accent flex items-center gap-2">
              <UsdtIcon width={40} height={40} className="shrink-0" />
              {formatBalance(claimableNowValue, { roundForFiat: true })}
            </span>
          )}
        </div>

        {isLoading ? (
          <div className="flex flex-col gap-3">
            <SkeletonElement className="h-5 w-full rounded-md" />
            <SkeletonElement className="h-2.5 w-full rounded-full" />
          </div>
        ) : claimableSoonValue > 0 && timeRemaining ? (
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium tracking-wide text-cede-text-secondary">Next unlock in</span>
              <span className="text-base font-bold tracking-wide text-cede-text-primary">{timeRemaining}</span>
            </div>
            <div className="relative h-2.5 w-full overflow-hidden rounded-full bg-contrast-shade-primary-30">
              <div
                className="absolute inset-y-0 left-0 rounded-full bg-cede-primary transition-all duration-300 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        ) : claimableSoonValue === 0 ? (
          <div className="flex items-center justify-center py-2">
            <span className="text-sm italic text-cede-text-secondary">
              No rewards to be claimed soon. Join campaigns to earn more!
            </span>
          </div>
        ) : null}
      </div>

      <div className="flex flex-col">
        <ClaimRewards fullWidth />
      </div>
    </InfoCard>
  );
};
